import { ref, Ref } from 'vue';
import { MasterDataApiCaller, MasterDataOption } from '../utils/MasterDataApiCaller';

/**
 * Session-cached master data values composable
 * Loads master data options once per browser session and caches them in memory
 *
 * Usage:
 * ```typescript
 * import { useMasterData } from '@/composables/useMasterData';
 *
 * const { marketplaceOptions, loadAllMasterData } = useMasterData();
 *
 * // In onMounted
 * await loadAllMasterData();
 *
 * // Use in v-select
 * <v-select :items="marketplaceOptions" item-title="code" item-value="code" />
 * ```
 */

// Global state - persists across component instances for session duration
const marketplaceOptions: Ref<MasterDataOption[]> = ref([]);
const currencyOptions: Ref<MasterDataOption[]> = ref([]);
const categoryOptions: Ref<MasterDataOption[]> = ref([]);

// Loading state
const isLoading: Ref<boolean> = ref(false);
const isLoaded: Ref<boolean> = ref(false);
const loadError: Ref<string> = ref('');

/**
 * Composable function for accessing master data options
 * Returns reactive refs that are shared across all component instances
 */
export function useMasterData() {
    /**
     * Load all master data options from API
     * Only loads once per session - subsequent calls return immediately
     * @returns Promise that resolves when all master data is loaded
     */
    const loadAllMasterData = async (): Promise<void> => {
        // Skip if already loaded or currently loading
        if (isLoaded.value || isLoading.value) {
            return;
        }

        isLoading.value = true;
        loadError.value = '';

        try {
            // Load all master data in parallel for better performance
            const [marketplace, currency, category] = await Promise.all([
                MasterDataApiCaller.getMarketplaceOptions(),
                MasterDataApiCaller.getCurrencyOptions(),
                MasterDataApiCaller.getCategoryOptions()
            ]);

            // Update reactive refs
            marketplaceOptions.value = marketplace;
            currencyOptions.value = currency;
            categoryOptions.value = category;

            isLoaded.value = true;

            console.log('✅ Master data loaded successfully:', {
                marketplace: marketplace.length,
                currency: currency.length,
                category: category.length
            });
        } catch (error) {
            loadError.value = error instanceof Error ? error.message : 'Failed to load master data options';
            console.error('❌ Failed to load master data:', error);

            // Set empty arrays as fallback
            marketplaceOptions.value = [];
            currencyOptions.value = [];
            categoryOptions.value = [];
        } finally {
            isLoading.value = false;
        }
    };

    /**
     * Force reload all master data options
     * Use this if you need to refresh master data
     */
    const reloadMasterData = async (): Promise<void> => {
        isLoaded.value = false;
        await loadAllMasterData();
    };

    /**
     * Clear all cached master data
     * Useful for testing or when user logs out
     */
    const clearMasterData = (): void => {
        marketplaceOptions.value = [];
        currencyOptions.value = [];
        categoryOptions.value = [];
        isLoaded.value = false;
        isLoading.value = false;
        loadError.value = '';
    };

    return {
        // Master data options (reactive refs)
        marketplaceOptions,
        currencyOptions,
        categoryOptions,

        // Loading state
        isLoading,
        isLoaded,
        loadError,

        // Methods
        loadAllMasterData,
        reloadMasterData,
        clearMasterData
    };
}
